
import { useEffect, useMemo, useRef, useState } from 'react';

//interfaces
import { ISimpleTableColumn, ISimpleTableProps, ISimpleTableRows, ProfileImage} from 'thrive-components';
import { ILegacyAppUser } from  '@/interfaces/passkeys';

//api
import { listLegacyAppUsers } from '@/api/orgSettings/passkeys';



const UseLegacyAppUsers = (orgId: any, showModal: boolean) => {
    const simpleBarRef = useRef();
    const [tableHeight, setTableHeight] = useState<number>(88)
    const [sortBy, setSortBy]=useState<any>()
    const [userData, setUserData] = useState<ILegacyAppUser[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [moreUsersAvailable, setMoreUsersAvailable] = useState<boolean>(false);
    const [skip, setSkip]=useState<number>(0);
    const autoScrollHandler =  !isLoading && !isLoadingMore && moreUsersAvailable;
    const emptyTableMessage = "No users to display";
    const columns: Array<ISimpleTableColumn> = [
        { 
            title: "Name", 
            isToggleButton: true,                    
            onSortAscendingClick: () => setSortBy("desc"), 
            onSortDescendingClick: () => setSortBy("asc"), 
        },
    ];

    const tableContent = useMemo<ISimpleTableProps>(() => (
        {
            simpleTableContent: {columns: columns, rows: formatTableRows()},
            isInfiniteScroll: true,
            isLoading: isLoading,
            noDataMessage: emptyTableMessage,
            getMoreContent: getMoreUsers,
            infiniteScrollFetchCondition: moreUsersAvailable,
            hideActionsColumn: true
        }), [ userData, isLoading]
    );

    useEffect(() => {
        if (showModal) {
            getUserData();
        } else {
            cleanUp();
        }
    },[sortBy, showModal])

    useEffect(() => {
        if (userData?.length > 0) {
            setTableHeight(getTableHeight())
        } else {
            setTableHeight(88);
        }
    },[userData]);

    function formatTableRows(){
        return userData.map((user: ILegacyAppUser) => ({
            content: [
                <div className='legacy-user-details'>
                    <ProfileImage
                        profileImageUrl={user.profileImage}
                        className={"user-image"} 
                    />
                    <span className='legacy-user-name'>{user.displayName}</span>
                </div>
            ],
            controls : []
        })) as ISimpleTableRows[]
    }

    function getTableHeight(): number {
        const rows = formatTableRows();
        if(!rows.length) {
            return  88
        }
        return rows.length >= 9 ? 400 : rows.length * 40 + 35
    }

    function getMoreUsers() {
        if (isLoadingMore) { return }
        setIsLoadingMore(true);
        getUserData(true);
    }

    function getUserData(pagination?: boolean) {
        if (!showModal) { return}
        if (!pagination) { setIsLoading(true);}

        const skipCount = pagination ? skip : 0;
        let params : any = { top: 20, skip: skipCount}

        if (sortBy) {
            params = {
                ...params, 
                sortBy: 'displayname', 
                sort: sortBy
            }
        }

        listLegacyAppUsers(orgId, params)
        .then((data) => {
            if (pagination) {
                setUserData((prevUserData: any) => [...(prevUserData || []), ...data.items]);
            } else {
                setUserData(data.items);
            }
            setMoreUsersAvailable(data.totalItemCount > (skipCount + data.items.length));
            setSkip(data.nextSkip);
        })
        .catch(() => {
            console.error("Could not source Legacy Users")
        })
        .finally(() =>{
            setIsLoading(false);
            setIsLoadingMore(false);
        });
    }

    function cleanUp(){
        setUserData([]);
        setMoreUsersAvailable(false);
        setSkip(0);
        setSortBy(undefined)
    }
    
    return {
        tableContent,
        simpleBarRef,
        autoScrollHandler,
        tableHeight,
        getMoreUsers
    }
}

export default UseLegacyAppUsers;